import { configureStore } from "@reduxjs/toolkit";
import { AppState, rootReducer } from "Data/Redux/Reducers";
import storage from "redux-persist/lib/storage";
import {
	FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, PersistConfig, createTransform, persistStore,
	persistReducer
} from "redux-persist";
import { TypedUseSelectorHook, useSelector as reduxUseSelector, useDispatch as reduxUseDispatch } from "react-redux";
import { IItem, PaginatedDataSet } from "Interfaces";
import produce from "immer";
import { defaultPaginatedStateOptions } from "Data/Objects/Redux";
import listenerMiddleware from "Data/Redux/Middleware/Listener";
import { disconnectOnLogout, logoutOnUnauthorized } from "Data/Redux/Middleware/Middleware";

export type AsyncThunkConfig = {
	state: AppState;
};

const persistStateTransform = createTransform(
	(inboundState, key) => {
		switch (key) {
			case "Items": {
				const typedState: PaginatedDataSet<IItem> = inboundState as PaginatedDataSet<IItem>;

				return produce(typedState, (draft) => {
					draft.data = [];
					draft.lastPage = -1;
					draft.options = { ...typedState?.options, ...defaultPaginatedStateOptions };
				});
			}

			default:
				return inboundState;
		}
	}
);

const persistConfig: PersistConfig<AppState> = {
	key: "root",
	blacklist: [ "Order" ],
	storage,
	transforms: [ persistStateTransform ]
};

export const store = configureStore({
	reducer: persistReducer(persistConfig, rootReducer),
	middleware: (getDefaultMiddleware) => getDefaultMiddleware({
		immutableCheck: false,
		serializableCheck: {
			ignoredActions: [ FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER ]
		}
	}).prepend(listenerMiddleware.middleware, disconnectOnLogout, logoutOnUnauthorized)
});

export const persistedStore = persistStore(store);

export const useAppSelector: TypedUseSelectorHook<AppState> = reduxUseSelector;
export const useAppDispatch: () => typeof store.dispatch = reduxUseDispatch;
